import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { User, FileEdit } from 'lucide-react';
import { useConfig } from '../../contexts/ConfigContext';
import { getSpecializationTerm, getSpecializationPrefix } from '../../utils/organizationHelpers';
import { useIframeCommunication } from '../../hooks/useIframeCommunication';
import GroupsList from '../../components/GroupsList';
import Footer from '../../components/Footer';
import { formatUserDisplayName } from '../../utils/userDisplayName';
import './TeacherProfilePage.css';

const TeacherProfilePage = () => {
    // Общение с родительским окном через postMessage
    useIframeCommunication();
    const { id } = useParams();
    const { config } = useConfig();
    const orgCode = config?.code || 'mag';
    const [selectedDirection, setSelectedDirection] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const [parentUrlOverride, setParentUrlOverride] = useState('');

    // Определяем, встроена ли страница в iframe и берем URL родителя
    const isIframe = useMemo(() => window.self !== window.top, []);
    const parentUrl = useMemo(() => (isIframe ? document.referrer || '' : ''), [isIframe]);
    const effectiveParentUrl = parentUrlOverride || parentUrl;

    // Слушаем postMessage от родителя с полным URL
    useEffect(() => {
        if (!isIframe) return;
        const handler = (event) => {
            if (typeof event.data === 'string' && event.data.startsWith('http')) {
                setParentUrlOverride(event.data);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [isIframe]);

    // Загрузка профиля преподавателя
    const { data: profile, isLoading: profileLoading } = useQuery(
        ['teacherProfile', id, effectiveParentUrl, isIframe],
        async () => {
            if (isIframe) {
                if (!effectiveParentUrl) return null;
                const response = await axios.get(`${API_BASE_URL}/users/public/by-site`, {
                    params: { url: effectiveParentUrl }
                });
                return response.data;
            } else {
                const response = await axios.get(`${API_BASE_URL}/users/public/${id}`);
                return response.data;
            }
        },
        {
            retry: 1,
            enabled: isIframe ? !!effectiveParentUrl : !!id,
        }
    );

    // Загрузка групп преподавателя
    const { data: allGroupsRaw, isLoading: groupsLoading } = useQuery(
        ['teacherGroups', profile?.id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/users/${profile.id}/teacher-groups`);
            return response.data;
        },
        { enabled: !!profile?.id }
    );

    // Фильтруем группы: open/started_additional; с расписанием или с форматом "регулярно"
    const allGroups = React.useMemo(() => {
        if (!allGroupsRaw) return [];
        return allGroupsRaw.filter(group => {
            const status = group.recruitment_status;
            if (status !== 'open' && status !== 'started_additional') return false;
            const hasRegularFormat = group.format_data?.[0]?.frequency === 'weekly' || group.format_data?.[0]?.frequency === 'biweekly';
            return group.earliest_event_date || hasRegularFormat;
        });
    }, [allGroupsRaw]);

    // Загрузка регионов и направлений для фильтров
    const { data: allRegions } = useQuery('publicRegions', () =>
        axios.get(`${API_BASE_URL}/regions/public`).then(res => res.data)
    );

    const { data: directions } = useQuery('publicDirections', () =>
        axios.get(`${API_BASE_URL}/directions/public`).then(res => res.data)
    );

    // Загрузка статей пользователя
    const { data: userArticles } = useQuery(
        ['userArticles', id],
        async () => {
            const response = await axios.get(`${API_BASE_URL}/users/${id}/articles`);
            return response.data;
        },
        {
            enabled: !!id,
            retry: 1
        }
    );

    // Группируем направления - объединяем все "Специализация: *" / "Авторская программа: *" в одно
    const displayDirections = React.useMemo(() => {
        if (!directions || !allGroups) return [];

        const prefix = getSpecializationPrefix(orgCode);
        const specializationTerm = getSpecializationTerm(orgCode);

        // Получаем уникальные ID направлений, для которых есть группы
        const availableDirectionIds = new Set(allGroups.map(g => g.direction_id).filter(Boolean));

        // Фильтруем только доступные направления
        const availableDirections = directions.filter(d => availableDirectionIds.has(d.id));

        const specializations = availableDirections.filter(d => d.name.startsWith(prefix));
        const otherDirections = availableDirections.filter(d => !d.name.startsWith(prefix));

        const result = [];

        // Добавляем объединенную кнопку "Специализация" / "Авторская программа", если есть такие направления
        if (specializations.length > 0) {
            result.push({
                id: 'specialization',
                name: specializationTerm,
                directionIds: specializations.map(s => s.id)
            });
        }

        // Добавляем остальные направления
        otherDirections.forEach(d => {
            result.push({
                id: d.id,
                name: d.name,
                directionIds: [d.id]
            });
        });

        return result;
    }, [directions, allGroups, orgCode]);

    // Формируем список ID направлений для фильтрации
    const selectedDirectionIds = React.useMemo(() => {
        if (!selectedDirection) return null;
        const direction = displayDirections.find(d => d.id === selectedDirection);
        return direction ? direction.directionIds : null;
    }, [selectedDirection, displayDirections]);

    // Фильтрация групп на клиенте
    const groups = React.useMemo(() => {
        if (!allGroups) return [];

        return allGroups.filter(group => {
            // Фильтр по направлению
            if (selectedDirectionIds && !selectedDirectionIds.includes(group.direction_id)) {
                return false;
            }

            // Фильтр по региону
            if (selectedRegion && group.region_id !== selectedRegion) {
                return false;
            }

            return true;
        });
    }, [allGroups, selectedDirectionIds, selectedRegion]);

    // Фильтруем регионы - только Москва, Онлайн и "Регионы России"
    const regions = allRegions?.filter(r =>
        r.name === 'Москва' || r.name === 'Онлайн' || r.name === 'Регионы России'
    ) || [];

    // Переименовываем "Регионы России" в "Регионы" для отображения
    const displayRegions = regions.map(r => ({
        ...r,
        displayName: r.name === 'Регионы России' ? 'Регионы' : r.name
    }));

    const handleDirectionClick = (directionId) => {
        setSelectedDirection(selectedDirection === directionId ? null : directionId);
    };

    const handleRegionClick = (regionId) => {
        setSelectedRegion(selectedRegion === regionId ? null : regionId);
    };

    if (profileLoading || groupsLoading) {
        return (
            <div className="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="error-container">
                <h2>Преподаватель не найден</h2>
            </div>
        );
    }

    return (
        <div className="teacher-profile-page">
            {/* Профиль преподавателя — скрываем в iframe */}
            {!isIframe && (
                <div className="profile-header">
                    <div className="profile-photo-section">
                        {profile.photo_url ? (
                            <img
                                src={profile.photo_url.startsWith('/uploads/')
                                    ? `${window.location.origin}${profile.photo_url}`
                                    : profile.photo_url}
                                alt={formatUserDisplayName(profile)}
                                className="profile-photo"
                            />
                        ) : (
                            <div className="profile-photo-placeholder">
                                <User size={80} />
                            </div>
                        )}
                    </div>
                    <div className="profile-info-section">
                        <h1>{formatUserDisplayName(profile)}</h1>
                        {profile.short_description && profile.short_description !== 'null' && (
                            <p className="teacher-profile-short-description">{profile.short_description}</p>
                        )}

                        {/* Дополнительная информация */}
                        <div className="profile-meta-info">
                            {profile.city && profile.city.length > 0 && (
                                <div className="meta-info-item">
                                    <span className="meta-label">Города:</span>
                                    <div className="meta-tags">
                                        {profile.city.map((city, idx) => (
                                            <span key={idx} className="meta-tag">{city}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {profile.language && profile.language.length > 0 && (
                                <div className="meta-info-item">
                                    <span className="meta-label">Языки:</span>
                                    <div className="meta-tags">
                                        {profile.language.map((lang, idx) => (
                                            <span key={idx} className="meta-tag">{lang}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {profile.work_format && profile.work_format.length > 0 && (
                                <div className="meta-info-item">
                                    <span className="meta-label">Форматы работы:</span>
                                    <div className="meta-tags">
                                        {profile.work_format.map((format, idx) => (
                                            <span key={idx} className="meta-tag">{format}</span>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {profile.description && profile.description !== 'null' && profile.description !== formatUserDisplayName(profile) && (
                            <div className="description">
                                <h3>О преподавателе</h3>
                                <p>{profile.description}</p>
                            </div>
                        )}

                        {profile.site_page_url && (
                            <div style={{ marginTop: '12px' }}>
                                <a
                                    className="teacher-profile-more-link"
                                    href={profile.site_page_url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Подробнее
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Фильтры и группы - точно как на MainPage */}
            <div className="filter-buttons">
                <h4>Группы преподавателя:</h4>
                <br />
                {displayDirections.length > 0 && (
                    <>
                        <div className="button-row">
                            {displayDirections.map(direction => (
                                <button
                                    key={direction.id}
                                    className={selectedDirection === direction.id ? 'active-filter' : ''}
                                    onClick={() => handleDirectionClick(direction.id)}
                                >
                                    {direction.name}
                                </button>
                            ))}
                        </div>
                        <br />
                    </>
                )}

                {displayRegions.length > 0 && (
                    <>
                        <h4>Место проведения группы:</h4>
                        <br />
                        <div className="button-row">
                            {displayRegions.map(region => (
                                <button
                                    key={region.id}
                                    className={selectedRegion === region.id ? 'active-filter' : ''}
                                    onClick={() => handleRegionClick(region.id)}
                                >
                                    {region.displayName}
                                </button>
                            ))}
                        </div>
                        <br />
                    </>
                )}
                <hr width="100%" />
                <br />
            </div>

            {/* Плитки групп - точно как на MainPage */}
            <GroupsList groups={groups} loading={groupsLoading} orgCode={orgCode} />

            {/* Статьи пользователя */}
            {userArticles && userArticles.length > 0 && (
                <div className="teacher-articles-section">
                    <h2>
                        <FileEdit size={24} />
                        Статьи
                    </h2>
                    <div className="teacher-articles-grid">
                        {userArticles.map(article => (
                            <Link
                                key={article.id}
                                to={`/public/articles/${article.id}`}
                                className="teacher-article-card"
                            >
                                {article.cover_image_url && (
                                    <div className="teacher-article-image">
                                        <img src={article.cover_image_url} alt={article.title} />
                                    </div>
                                )}
                                <div className="teacher-article-content">
                                    <h3 className="teacher-article-title">{article.title}</h3>
                                    {article.excerpt && (
                                        <p className="teacher-article-excerpt">{article.excerpt}</p>
                                    )}
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
            <Footer />
        </div>
    );
};

export default TeacherProfilePage;
